import React from 'react';
import { 
  Box, 
  CircularProgress, 
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Fade,
  Chip
} from '@mui/material';
import { LoadingState } from '../../hooks/useLoadingState';

interface LoadingSpinnerProps {
  message?: string;
  size?: number;
  progress?: number;
  showProgress?: boolean;
  loadingState?: LoadingState;
  variant?: 'circular' | 'linear' | 'card';
  fullScreen?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  message = 'Loading...', 
  size = 40,
  progress,
  showProgress = false,
  loadingState,
  variant = 'circular',
  fullScreen = false
}) => {
  // Use loadingState if provided, otherwise use individual props
  const displayMessage = loadingState?.message || message;
  const displayProgress = loadingState?.progress ?? progress;
  const hasProgress = showProgress || displayProgress !== undefined;
  const operation = loadingState?.operation;
  const estimatedCompletion = loadingState?.estimatedCompletion;

  // Calculate estimated time remaining
  const getTimeRemaining = (): string | null => {
    if (!estimatedCompletion) return null;
    
    const remaining = Math.max(0, estimatedCompletion - Date.now());
    const seconds = Math.ceil(remaining / 1000);
    
    if (seconds < 60) return `${seconds}s remaining`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes}m remaining`;
  };

  const timeRemaining = getTimeRemaining();

  const content = (
    <>
      {variant === 'circular' && (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress 
            size={size} 
            variant={hasProgress ? 'determinate' : 'indeterminate'}
            value={displayProgress}
          />
          {hasProgress && displayProgress !== undefined && (
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
                sx={{ fontSize: size / 6 }}
              >
                {Math.round(displayProgress)}%
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {variant === 'linear' && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress 
            variant={hasProgress ? 'determinate' : 'indeterminate'}
            value={displayProgress}
          />
          {hasProgress && displayProgress !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.round(displayProgress)}%
              </Typography>
              {timeRemaining && (
                <Typography variant="body2" color="text.secondary">
                  {timeRemaining}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {displayMessage && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mt: variant === 'circular' ? 2 : 1, textAlign: 'center' }}
        >
          {displayMessage}
        </Typography>
      )}

      {operation && operation !== displayMessage && (
        <Chip
          label={operation}
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      )}
    </>
  );

  if (variant === 'card') {
    return (
      <Fade in={true}>
        <Card sx={{ maxWidth: 400, mx: 'auto', my: 4 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 2,
              }}
            >
              {content}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  }

  const containerSx = fullScreen ? {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 9999,
  } : {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    py: 4,
  };

  return (
    <Fade in={true}>
      <Box sx={containerSx}>
        {content}
      </Box>
    </Fade>
  );
};

/**
 * Multi-operation loading display component
 */
interface MultiOperationLoadingProps {
  operations: Map<string, LoadingState>;
  maxVisible?: number;
  showProgress?: boolean;
}

export const MultiOperationLoading: React.FC<MultiOperationLoadingProps> = ({
  operations,
  maxVisible = 3,
  showProgress = true
}) => {
  const loadingOps = Array.from(operations.entries())
    .filter(([_, state]) => state.isLoading)
    .slice(0, maxVisible);

  if (loadingOps.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {loadingOps.map(([operationId, state]) => (
        <Box key={operationId} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography variant="body2">
              {state.message || state.operation || operationId}
            </Typography>
          </Box>
          
          {showProgress && state.progress !== undefined && (
            <LinearProgress 
              variant="determinate" 
              value={state.progress}
              sx={{ ml: 4 }}
            />
          )}
        </Box>
      ))}
      
      {operations.size > maxVisible && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
          +{operations.size - maxVisible} more operations...
        </Typography>
      )}
    </Box>
  );
};